import type Luminary from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { formatAsset, formatProperties } from './serialization/assetFormatters';
import {
    getSceneDescriptions,
    getSceneGroupDescriptions,
    getTemplateDescription,
    getVideoDescription,
    getVideoMusicDescription,
} from './serialization';
import type {
    AssetChanges,
    AssetDescription,
    ModifiedAsset,
    ModifiedScene,
    ModifiedSceneGroup,
    MusicChanges,
    PropertyChange,
    SceneChanges,
    SceneDescription,
    SceneGroupChanges,
    SceneGroupDescription,
    TemplateChanges,
    TranscriptDescription,
    VideoChanges,
    VideoDiff,
    VideoStateSnapshot,
} from './types';

import type { MediaCandidates } from 'js/src/client';

/**
 * Capture a snapshot of the current video state from Luminary
 */
export async function captureVideoState(
    luminary: Luminary,
    mediaCandidates?: MediaCandidates | null,
): Promise<VideoStateSnapshot> {
    // Wait for Luminary to fully load assets before capturing state
    await luminary.onAssetsDoneTryingToLoad();

    const videoDesc = getVideoDescription(luminary);
    const templateDesc = getTemplateDescription(luminary);
    const musicDesc = getVideoMusicDescription(luminary);

    // Capture scenes
    const scenes: SceneDescription[] = [];
    for (const scene of luminary.scenes) {
        if (scene.sceneCategory === VideoData.CATEGORY_BRAND) {
            continue;
        }

        const sceneDesc = getSceneDescriptions(scene, luminary);
        scenes.push(sceneDesc);
    }

    // Capture scene groups
    const sceneGroups = getSceneGroupDescriptions(luminary);

    return {
        timestamp: Date.now(),
        video: videoDesc,
        template: templateDesc,
        music: musicDesc,
        scenes,
        sceneGroups,
    };
}

/**
 * Compute the diff between two video state snapshots
 */
export function computeVideoDiff(
    prevState: VideoStateSnapshot,
    currentState: VideoStateSnapshot,
): VideoDiff {
    const diff: VideoDiff = {
        diffType: 'incremental',
        baselineTimestamp: prevState.timestamp,
        currentTimestamp: currentState.timestamp,
        changes: {
            scenes: {
                added: [],
                removed: [],
                modified: [],
            },
        },
    };

    // Check video-level changes
    const videoChanges = computeObjectDiff(prevState.video, currentState.video);
    if (Object.keys(videoChanges).length > 0) {
        diff.changes.video = videoChanges as VideoChanges;
    }

    // Check template changes
    const templateChanges = computeObjectDiff(prevState.template, currentState.template);
    if (Object.keys(templateChanges).length > 0) {
        diff.changes.template = templateChanges as TemplateChanges;
    }

    // Check music changes
    const musicChanges = computeObjectDiff(prevState.music, currentState.music);
    if (Object.keys(musicChanges).length > 0) {
        diff.changes.music = musicChanges as MusicChanges;
    }

    // Compute scene changes
    const sceneChanges = computeSceneChanges(prevState.scenes, currentState.scenes);
    diff.changes.scenes = sceneChanges;

    // Compute scene group changes
    const sceneGroupChanges = computeSceneGroupChanges(
        prevState.sceneGroups,
        currentState.sceneGroups,
    );
    if (
        sceneGroupChanges.added.length > 0 ||
        sceneGroupChanges.removed.length > 0 ||
        sceneGroupChanges.modified.length > 0
    ) {
        diff.changes.sceneGroups = sceneGroupChanges;
    }

    return diff;
}

/**
 * Helper to compute property changes between two objects
 */
function computeObjectDiff<T extends Record<string, unknown>>(
    prev: T,
    current: T,
): Record<string, PropertyChange<unknown>> {
    const changes: Record<string, PropertyChange<unknown>> = {};

    for (const key in current) {
        if (prev[key] !== current[key]) {
            changes[key] = {
                from: prev[key],
                to: current[key],
            };
        }
    }

    return changes;
}

/**
 * Compute scene-level changes
 */
function computeSceneChanges(
    prevScenes: SceneDescription[],
    currentScenes: SceneDescription[],
): SceneChanges {
    const changes: SceneChanges = {
        added: [],
        removed: [],
        modified: [],
    };

    // Create maps for efficient lookup
    const prevSceneMap = new Map(prevScenes.map((s) => [s.globalIndex, s]));
    const currentSceneMap = new Map(currentScenes.map((s) => [s.globalIndex, s]));

    // Find added scenes
    for (const scene of currentScenes) {
        if (!prevSceneMap.has(scene.globalIndex)) {
            changes.added.push(scene);
        }
    }

    // Find removed and modified scenes
    for (const prevScene of prevScenes) {
        const currentScene = currentSceneMap.get(prevScene.globalIndex);

        if (!currentScene) {
            changes.removed.push(prevScene);
        } else {
            // Check for modifications
            const sceneModifications = computeSceneModifications(prevScene, currentScene);
            if (sceneModifications) {
                changes.modified.push(sceneModifications);
            }
        }
    }

    return changes;
}

/**
 * Compute modifications within a single scene
 */
function computeSceneModifications(
    prevScene: SceneDescription,
    currentScene: SceneDescription,
): ModifiedScene | null {
    const modifications: ModifiedScene = {
        globalIndex: currentScene.globalIndex,
        changes: {
            assets: {
                added: [],
                removed: [],
                modified: [],
            },
        },
    };

    let hasChanges = false;

    // Check scene-level property changes
    if (prevScene.design !== currentScene.design) {
        modifications.changes.sceneDesign = {
            from: prevScene.design,
            to: currentScene.design,
        };
        hasChanges = true;
    }

    if (prevScene.duration !== currentScene.duration) {
        modifications.changes.duration = {
            from: prevScene.duration,
            to: currentScene.duration,
        };
        hasChanges = true;
    }

    if (prevScene.subscenes !== currentScene.subscenes) {
        modifications.changes.subscenes = {
            from: prevScene.subscenes,
            to: currentScene.subscenes,
        };
        hasChanges = true;
    }

    if (
        JSON.stringify(prevScene.textPositionPresets) !==
        JSON.stringify(currentScene.textPositionPresets)
    ) {
        modifications.changes.textPositionPresets = {
            from: prevScene.textPositionPresets,
            to: currentScene.textPositionPresets,
        };
        hasChanges = true;
    }

    // Compute asset changes
    const assetChanges = computeAssetChanges(prevScene.assets, currentScene.assets);
    modifications.changes.assets = assetChanges;

    if (
        assetChanges.added.length > 0 ||
        assetChanges.removed.length > 0 ||
        assetChanges.modified.length > 0
    ) {
        hasChanges = true;
    }

    return hasChanges ? modifications : null;
}

/**
 * Compute asset-level changes within a scene
 */
function computeAssetChanges(
    prevAssets: AssetDescription[],
    currentAssets: AssetDescription[],
): AssetChanges {
    const changes: AssetChanges = {
        added: [],
        removed: [],
        modified: [],
    };

    // Create maps for efficient lookup
    const prevAssetMap = new Map(prevAssets.map((a) => [a.name, a]));
    const currentAssetMap = new Map(currentAssets.map((a) => [a.name, a]));

    // Find added assets
    for (const asset of currentAssets) {
        if (!prevAssetMap.has(asset.name)) {
            changes.added.push(asset);
        }
    }

    // Find removed and modified assets
    for (const prevAsset of prevAssets) {
        const currentAsset = currentAssetMap.get(prevAsset.name);

        if (!currentAsset) {
            changes.removed.push(prevAsset);
        } else {
            // Check for modifications
            const assetModifications = computeAssetModifications(prevAsset, currentAsset);
            if (assetModifications) {
                changes.modified.push(assetModifications);
            }
        }
    }

    return changes;
}

/**
 * Compute modifications within a single asset
 */
function computeAssetModifications(
    prevAsset: AssetDescription,
    currentAsset: AssetDescription,
): ModifiedAsset | null {
    const propertyChanges: Record<string, PropertyChange<unknown>> = {};

    // Check basic properties
    if (prevAsset.type !== currentAsset.type) {
        propertyChanges.type = { from: prevAsset.type, to: currentAsset.type };
    }

    if (prevAsset.bounds !== currentAsset.bounds) {
        propertyChanges.bounds = { from: prevAsset.bounds, to: currentAsset.bounds };
    }

    if ('subscene' in prevAsset || 'subscene' in currentAsset) {
        const prevSubscene = 'subscene' in prevAsset ? prevAsset.subscene : undefined;
        const currentSubscene = 'subscene' in currentAsset ? currentAsset.subscene : undefined;
        if (prevSubscene !== currentSubscene) {
            propertyChanges.subscene = { from: prevSubscene, to: currentSubscene };
        }
    }

    // Check all asset-specific properties by comparing the full objects
    // We exclude 'name', 'type', 'bounds', and 'subscene' as we've already checked them
    const allKeys = new Set([...Object.keys(prevAsset), ...Object.keys(currentAsset)]);

    for (const key of allKeys) {
        // Skip already-checked properties
        if (key === 'name' || key === 'type' || key === 'bounds' || key === 'subscene') {
            continue;
        }

        const prevValue = (prevAsset as Record<string, unknown>)[key];
        const currentValue = (currentAsset as Record<string, unknown>)[key];

        // Deep comparison for objects/arrays
        if (JSON.stringify(prevValue) !== JSON.stringify(currentValue)) {
            propertyChanges[key] = { from: prevValue, to: currentValue };
        }
    }

    return Object.keys(propertyChanges).length > 0
        ? { name: currentAsset.name, changes: propertyChanges }
        : null;
}

/**
 * Compute scene group-level changes
 */
function computeSceneGroupChanges(
    prevGroups: SceneGroupDescription[],
    currentGroups: SceneGroupDescription[],
): SceneGroupChanges {
    const changes: SceneGroupChanges = { added: [], removed: [], modified: [] };

    const prevMap = new Map(prevGroups.map((g) => [g.index, g]));
    const currentMap = new Map(currentGroups.map((g) => [g.index, g]));

    // Find added groups
    for (const group of currentGroups) {
        if (!prevMap.has(group.index)) {
            changes.added.push(group);
        }
    }

    // Find removed and modified groups
    for (const prevGroup of prevGroups) {
        const currentGroup = currentMap.get(prevGroup.index);
        if (!currentGroup) {
            changes.removed.push(prevGroup);
        } else {
            const modifications = computeSceneGroupModifications(prevGroup, currentGroup);
            if (modifications) {
                changes.modified.push(modifications);
            }
        }
    }

    return changes;
}

/**
 * Compute modifications within a single scene group
 */
function computeSceneGroupModifications(
    prev: SceneGroupDescription,
    current: SceneGroupDescription,
): ModifiedSceneGroup | null {
    const modifications: ModifiedSceneGroup = {
        index: current.index,
        changes: {},
    };

    let hasChanges = false;

    if (prev.type !== current.type) {
        modifications.changes.type = { from: prev.type, to: current.type };
        hasChanges = true;
    }

    if (JSON.stringify(prev.sceneIndices) !== JSON.stringify(current.sceneIndices)) {
        modifications.changes.sceneIndices = { from: prev.sceneIndices, to: current.sceneIndices };
        hasChanges = true;
    }

    if (prev.duration !== current.duration) {
        modifications.changes.duration = { from: prev.duration, to: current.duration };
        hasChanges = true;
    }

    if (JSON.stringify(prev.transcript) !== JSON.stringify(current.transcript)) {
        modifications.changes.transcript = {
            from: prev.transcript,
            to: current.transcript,
        } as PropertyChange<TranscriptDescription | null>;
        hasChanges = true;
    }

    return hasChanges ? modifications : null;
}

/**
 * Format initial video state snapshot as a git-style diff showing all properties as additions.
 * Used to inject the starting video state into system messages.
 */
export function formatInitialStateAsGitDiff(snapshot: VideoStateSnapshot): string {
    let diffOutput = '```diff\n';
    diffOutput += '@@ Initial Video State @@\n';

    // Video properties
    diffOutput += formatProperties(snapshot.video, '+ ');

    // Template properties
    diffOutput += '@@ Template @@\n';
    diffOutput += formatProperties(snapshot.template, '+ ');

    // Music properties
    diffOutput += '@@ Music @@\n';
    diffOutput += formatProperties(snapshot.music, '+ ', ['representation']);

    // Scene Groups
    diffOutput += '@@ Scene Groups @@\n';
    for (const group of snapshot.sceneGroups) {
        diffOutput += formatSceneGroup(group, '+ ');
    }

    // Scenes
    for (const scene of snapshot.scenes) {
        diffOutput += `@@ Scene index ${scene.globalIndex} @@\n`;
        diffOutput += formatProperties(scene, '+ ', [
            'globalIndex',
            'assets',
            'textPositionPresets',
        ]);

        if (scene.textPositionPresets.length > 0) {
            diffOutput += `+ textPositionPresets: [${scene.textPositionPresets.join(', ')}]\n`;
        }

        for (const asset of scene.assets) {
            diffOutput += formatAsset(asset, '+ ');
        }
    }

    diffOutput += '```\n';
    return diffOutput;
}

/**
 * Format video diff as git-style patch with color-coded changes.
 * Used for tool results and system messages.
 */
export function formatVideoDiffAsGitPatch(diff: VideoDiff, sceneIndexFilter?: number): string {
    // Check if there are any changes at all
    const hasSceneGroupChanges =
        diff.changes.sceneGroups &&
        (diff.changes.sceneGroups.added.length > 0 ||
            diff.changes.sceneGroups.removed.length > 0 ||
            diff.changes.sceneGroups.modified.length > 0);

    const hasChanges =
        diff.changes.video ||
        diff.changes.template ||
        diff.changes.music ||
        diff.changes.scenes.added.length > 0 ||
        diff.changes.scenes.removed.length > 0 ||
        diff.changes.scenes.modified.length > 0 ||
        hasSceneGroupChanges;

    if (!hasChanges) {
        return ''; // No changes, don't add anything
    }

    let diffOutput = '```diff\n';

    // Video-level changes
    if (diff.changes.video) {
        diffOutput += '@@ Video Properties @@\n';
        for (const [key, change] of Object.entries(diff.changes.video)) {
            diffOutput += `- ${key}: ${String(change.from)}\n`;
            diffOutput += `+ ${key}: ${String(change.to)}\n`;
        }
    }

    // Template changes - skip if filtering by scene
    if (diff.changes.template && sceneIndexFilter === undefined) {
        diffOutput += '@@ Template Properties @@\n';
        for (const [key, change] of Object.entries(diff.changes.template)) {
            diffOutput += `- ${key}: ${String(change.from)}\n`;
            diffOutput += `+ ${key}: ${String(change.to)}\n`;
        }
    }

    // Music changes - skip if filtering by scene
    if (diff.changes.music && sceneIndexFilter === undefined) {
        diffOutput += '@@ Music Properties @@\n';
        for (const [key, change] of Object.entries(diff.changes.music)) {
            const fromStr =
                typeof change.from === 'object'
                    ? JSON.stringify(change.from)
                    : String(change.from ?? 'null');
            const toStr =
                typeof change.to === 'object'
                    ? JSON.stringify(change.to)
                    : String(change.to ?? 'null');
            diffOutput += `- ${key}: ${fromStr}\n`;
            diffOutput += `+ ${key}: ${toStr}\n`;
        }
    }

    // Scene group changes - skip if filtering by scene
    if (diff.changes.sceneGroups && sceneIndexFilter === undefined) {
        const {
            added: addedGroups,
            removed: removedGroups,
            modified: modifiedGroups,
        } = diff.changes.sceneGroups;

        // Added scene groups
        for (const addedGroup of addedGroups) {
            diffOutput += `@@ Scene Group ${addedGroup.index} (Added) @@\n`;
            diffOutput += formatSceneGroup(addedGroup, '+ ');
        }

        // Removed scene groups
        for (const removedGroup of removedGroups) {
            diffOutput += `@@ Scene Group ${removedGroup.index} (Removed) @@\n`;
            diffOutput += formatSceneGroup(removedGroup, '- ');
        }

        // Modified scene groups
        for (const modifiedGroup of modifiedGroups) {
            diffOutput += `@@ Scene Group ${modifiedGroup.index} (Modified) @@\n`;
            for (const [key, change] of Object.entries(modifiedGroup.changes)) {
                if (key === 'transcript') {
                    // Format transcript changes with full word timings
                    diffOutput += formatTranscript(
                        change.from as TranscriptDescription | null,
                        '- ',
                    );
                    diffOutput += formatTranscript(change.to as TranscriptDescription | null, '+ ');
                } else {
                    diffOutput += `- ${key}: ${formatPropertyValue(change.from)}\n`;
                    diffOutput += `+ ${key}: ${formatPropertyValue(change.to)}\n`;
                }
            }
        }
    }

    // Scene changes
    const { added, removed, modified } = diff.changes.scenes;

    // Filter scenes if sceneIndexFilter is provided
    const filteredAdded =
        sceneIndexFilter !== undefined
            ? added.filter((s) => s.globalIndex === sceneIndexFilter)
            : added;
    const filteredRemoved =
        sceneIndexFilter !== undefined
            ? removed.filter((s) => s.globalIndex === sceneIndexFilter)
            : removed;
    const filteredModified =
        sceneIndexFilter !== undefined
            ? modified.filter((s) => s.globalIndex === sceneIndexFilter)
            : modified;

    // Added scenes
    for (const addedScene of filteredAdded) {
        diffOutput += `@@ Scene index ${addedScene.globalIndex} (Added) @@\n`;
        diffOutput += `+ design: ${addedScene.design}\n`;
        diffOutput += `+ duration: ${addedScene.duration}\n`;
        diffOutput += `+ subscenes: ${addedScene.subscenes}\n`;

        if (addedScene.textPositionPresets.length > 0) {
            diffOutput += `+ textPositionPresets: [${addedScene.textPositionPresets.join(', ')}]\n`;
        }

        for (const asset of addedScene.assets) {
            diffOutput += formatAsset(asset, '+ ');
        }
    }

    // Removed scenes
    for (const removedScene of filteredRemoved) {
        diffOutput += `@@ Scene index ${removedScene.globalIndex} (Removed) @@\n`;
        diffOutput += `- design: ${removedScene.design}\n`;
        diffOutput += `- duration: ${removedScene.duration}\n`;

        for (const asset of removedScene.assets) {
            diffOutput += formatAsset(asset, '- ');
        }
    }

    // Modified scenes
    for (const modifiedScene of filteredModified) {
        diffOutput += `@@ Scene index ${modifiedScene.globalIndex} (Modified) @@\n`;

        // Scene property changes
        const { sceneDesign, duration, subscenes } = modifiedScene.changes;

        if (sceneDesign) {
            diffOutput += `- design: ${sceneDesign.from}\n`;
            diffOutput += `+ design: ${sceneDesign.to}\n`;
        }
        if (duration) {
            diffOutput += `- duration: ${duration.from}\n`;
            diffOutput += `+ duration: ${duration.to}\n`;
        }
        if (subscenes) {
            diffOutput += `- subscenes: ${subscenes.from}\n`;
            diffOutput += `+ subscenes: ${subscenes.to}\n`;
        }

        // Asset changes
        const {
            added: addedAssets,
            removed: removedAssets,
            modified: modifiedAssets,
        } = modifiedScene.changes.assets;

        // Added assets
        for (const asset of addedAssets) {
            diffOutput += formatAsset(asset, '+ ');
        }

        // Removed assets
        for (const asset of removedAssets) {
            diffOutput += formatAsset(asset, '- ');
        }

        // Modified assets
        for (const modifiedAsset of modifiedAssets) {
            diffOutput += `  asset: ${modifiedAsset.name}\n`;
            for (const [propKey, propChange] of Object.entries(modifiedAsset.changes)) {
                const fromStr = formatPropertyValue(propChange.from);
                const toStr = formatPropertyValue(propChange.to);
                diffOutput += `-   ${propKey}: ${fromStr}\n`;
                diffOutput += `+   ${propKey}: ${toStr}\n`;
            }
        }
    }

    diffOutput += '```\n';
    return diffOutput;
}

/**
 * Helper to format property values for display in diffs
 */
function formatPropertyValue(value: unknown): string {
    if (value === null) {
        return 'null';
    }
    if (value === undefined) {
        return 'undefined';
    }
    if (typeof value === 'string') {
        return `"${value}"`;
    } else {
        const jsonStr = JSON.stringify(value);
        // JSON.stringify can return undefined for certain values (like functions, undefined)
        if (jsonStr === undefined) {
            return 'undefined';
        }
        return jsonStr.length > 100 ? `${jsonStr.substring(0, 100)}...` : jsonStr;
    }
}

/**
 * Format a transcript for display in diffs - shows full word timings
 */
function formatTranscript(transcript: TranscriptDescription | null, prefix: string): string {
    if (!transcript) {
        return `${prefix}transcript: null\n`;
    }

    let output = `${prefix}transcript:\n`;
    output += `${prefix}  language: ${transcript.language}\n`;
    output += `${prefix}  words:\n`;
    for (const word of transcript.words) {
        output += `${prefix}    ${word.word}\n`;
    }
    return output;
}

/**
 * Format a scene group in YAML-style format for diffs
 */
function formatSceneGroup(group: SceneGroupDescription, prefix: string): string {
    let output = `${prefix}- index: ${group.index}\n`;
    output += `${prefix}  type: ${group.type}\n`;
    output += `${prefix}  scenes: [${group.sceneIndices.join(', ')}]\n`;
    output += `${prefix}  duration: ${group.duration}ms\n`;
    if (group.transcript) {
        // Indent transcript under the scene group
        output += formatTranscript(group.transcript, `${prefix}  `);
    }
    return output;
}
